import React, { useState, useEffect, useCallback } from 'react';
import {
  collection, query, where, getDocs, Timestamp,
  doc, updateDoc, writeBatch, serverTimestamp, orderBy, documentId,
} from 'firebase/firestore';
import { db } from '../servicios/firebase';
import { useAuth } from '../context/AuthContext';
import BotonVolver from '../components/ui/BotonVolver';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const METODOS_PAGO = [
  { id: 'transferencia', label: '🏦 Transferencia' },
  { id: 'efectivo',      label: '💵 Efectivo'      },
  { id: 'otro',          label: 'Otro'             },
];

const ESTADO_CFG = {
  pendiente: { label: 'Pendiente', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  parcial:   { label: 'Parcial',   color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  pagado:    { label: 'Pagado ✓',  color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const COMISION_ASIENTO = 16; // comisión de la plataforma por asiento (Ángeles del Puerto)
const fmt = (n) => `$${Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 2 })}`;
const hoy = new Date().toISOString().split('T')[0];

// Desglose de un boleto (espejo del "Desglose del pago" de Stripe):
// - Taquilla (efectivo): llega el precio completo (no pasó por Stripe).
// - Online: bruto − procesamiento Stripe (3.6% + $3) − IVA del fee − comisión
//   plataforma ($16/asiento). OJO: stripeFee guardado desde balance_transaction
//   ya INCLUYE la comisión de plataforma, por eso el neto correcto es stripeNet
//   directo (restar la comisión encima de stripeFee la descontaría dos veces).
const desgloseBoleto = (t) => {
  const bruto = Number(t.precioTotal || t.montoTotal || 0);
  const esTaquilla = t.metodoVenta === 'fisica' || t.metodoPago === 'efectivo_taquilla';
  if (esTaquilla) return { bruto, stripe: 0, plataforma: 0, neto: bruto };

  const asientos = (t.asientosIda?.length || t.asientos?.length || 1);
  const plataforma = t.comisionPlataforma != null ? Number(t.comisionPlataforma) : asientos * COMISION_ASIENTO;

  if (t.stripeNet != null) {
    // Datos reales de Stripe
    const neto = Math.max(0, Number(t.stripeNet));
    const stripe = Math.max(0, Number(t.stripeFee || 0) - plataforma); // procesamiento + IVA
    return { bruto, stripe, plataforma, neto };
  }
  // Estimación para boletos viejos sin fees guardados: 3.6% + $3 MXN + IVA 16%
  const procesamiento = bruto * 0.036 + 3;
  const stripe = procesamiento * 1.16;
  return { bruto, stripe, plataforma, neto: Math.max(0, bruto - stripe - plataforma) };
};

const netoDueno = (t) => desgloseBoleto(t).neto;

// ─────────────────────────────────────────────────────────────────────────────
export default function Liquidaciones() {
  const { user } = useAuth();

  const [tab, setTab] = useState('actual');

  // Período
  const [modo,     setModo]     = useState('quincena');
  const [mesAnio,  setMesAnio]  = useState(() => { const d = new Date(); return { mes: d.getMonth(), anio: d.getFullYear() }; });
  const [quincena, setQuincena] = useState(() => new Date().getDate() <= 15 ? '1-15' : '16-fin');
  const [fechaInicio, setFechaInicio] = useState(() => {
    const d = new Date(); const mm = String(d.getMonth() + 1).padStart(2, '0');
    return d.getDate() <= 15 ? `${d.getFullYear()}-${mm}-01` : `${d.getFullYear()}-${mm}-16`;
  });
  const [fechaFin, setFechaFin] = useState(() => {
    const d = new Date(); const mm = String(d.getMonth() + 1).padStart(2, '0');
    if (d.getDate() <= 15) return `${d.getFullYear()}-${mm}-15`;
    const ult = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    return `${d.getFullYear()}-${mm}-${ult}`;
  });
  const [loading,     setLoading]     = useState(true);
  const [vehiculos,   setVehiculos]   = useState([]);
  const [resumen,     setResumen]     = useState({});
  const [desglose,    setDesglose]    = useState({ bruto: 0, stripe: 0, plataforma: 0, neto: 0 });
  const [sinAtribuir, setSinAtribuir] = useState({ pax: 0, ingresos: 0 });
  const [grand,       setGrand]       = useState({ pax: 0, ingresos: 0, urbans: 0 });
  const [expandido,   setExpandido]   = useState(null);
  const [filtroUrban, setFiltroUrban] = useState('');

  // Historial
  const [historial,        setHistorial]        = useState([]);
  const [loadingHistorial, setLoadingHistorial] = useState(false);
  const [expandidoCorte,   setExpandidoCorte]   = useState(null);
  const [filtroDueno,      setFiltroDueno]      = useState('');

  // Modales
  const [modalCerrar,  setModalCerrar]  = useState(false);
  const [cerrando,     setCerrando]     = useState(false);
  const [modalPago,    setModalPago]    = useState(null);
  const [pagoForm,     setPagoForm]     = useState({ monto: '', fecha: hoy, metodo: 'transferencia', referencia: '', notas: '' });
  const [guardandoPago, setGuardandoPago] = useState(false);

  // Quincena → fechas
  useEffect(() => {
    if (modo !== 'quincena') return;
    const { mes, anio } = mesAnio;
    const mm = String(mes + 1).padStart(2, '0');
    if (quincena === '1-15') { setFechaInicio(`${anio}-${mm}-01`); setFechaFin(`${anio}-${mm}-15`); }
    else { const ult = new Date(anio, mes + 1, 0).getDate(); setFechaInicio(`${anio}-${mm}-16`); setFechaFin(`${anio}-${mm}-${ult}`); }
  }, [modo, mesAnio, quincena]);

  const mesAnterior  = () => setMesAnio(({ mes, anio }) => mes === 0  ? { mes: 11, anio: anio - 1 } : { mes: mes - 1, anio });
  const mesSiguiente = () => setMesAnio(({ mes, anio }) => mes === 11 ? { mes: 0,  anio: anio + 1 } : { mes: mes + 1, anio });

  // ── Cargar período actual ──────────────────────────────────────────────────
  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [duenosSnap, vehiculosSnap, usuariosSnap] = await Promise.all([
        getDocs(collection(db, 'duenos')),
        getDocs(collection(db, 'vehiculos')),
        getDocs(collection(db, 'usuarios')).catch(() => ({ docs: [] })),
      ]);

      const duenosMap = {}, vehiculosMap = {}, choferesMap = {};
      duenosSnap.docs.forEach(d    => { duenosMap[d.id]    = { id: d.id, ...d.data() }; });
      vehiculosSnap.docs.forEach(d => { vehiculosMap[d.id] = { id: d.id, ...d.data() }; });
      usuariosSnap.docs.forEach(d  => {
        const u = d.data();
        if (u.rol === 'chofer' && u.vehiculoNumero) choferesMap[String(u.vehiculoNumero)] = u.nombre;
      });
      setVehiculos(Object.values(vehiculosMap).sort((a, b) => (a.numero || 0) - (b.numero || 0)));

      // Tickets por fecha de viaje. Sencillo usa fechaViaje, redondo usa fechaIda → dos consultas.
      const [snapViaje, snapIda] = await Promise.all([
        getDocs(query(collection(db, 'tickets'), where('fechaViaje', '>=', fechaInicio), where('fechaViaje', '<=', fechaFin))),
        getDocs(query(collection(db, 'tickets'), where('fechaIda',   '>=', fechaInicio), where('fechaIda',   '<=', fechaFin))),
      ]);
      const ticketsById = {};
      [...snapViaje.docs, ...snapIda.docs].forEach(d => { ticketsById[d.id] = d; });
      const ticketDocs = Object.values(ticketsById).filter(d => d.data().estado !== 'reembolsado');

      let ingresosTotal = 0;
      const dTot = { bruto: 0, stripe: 0, plataforma: 0, neto: 0 };
      ticketDocs.forEach(d => {
        const dx = desgloseBoleto(d.data());
        ingresosTotal  += dx.neto;
        dTot.bruto      += dx.bruto;
        dTot.stripe     += dx.stripe;
        dTot.plataforma += dx.plataforma;
        dTot.neto       += dx.neto;
      });
      setDesglose(dTot);

      // Viajes para conocer la urban asignada (ida y regreso, para el reparto del redondo)
      const viajeIds = [...new Set(
        ticketDocs.flatMap(d => [d.data().viajeId, d.data().viajeRegresoId]).filter(Boolean)
      )];
      const viajesMap = {};
      if (viajeIds.length > 0) {
        const chunks = [];
        for (let i = 0; i < viajeIds.length; i += 30) chunks.push(viajeIds.slice(i, i + 30));
        await Promise.all(chunks.map(async chunk => {
          const snap = await getDocs(query(collection(db, 'viajes'), where(documentId(), 'in', chunk)));
          snap.docs.forEach(d => { viajesMap[d.id] = d.data(); });
        }));
      }

      // Escaneos QR (solo cuentan abordajes confirmados, no determinan ingreso)
      const ini = Timestamp.fromDate(new Date(fechaInicio));
      const fin = Timestamp.fromDate(new Date(fechaFin + 'T23:59:59'));
      const valSnap = await getDocs(query(
        collection(db, 'validaciones'), where('timestamp', '>=', ini), where('timestamp', '<=', fin)
      ));

      const mapaV = {};
      const agregarUrban = (num, duenoId) => {
        if (!mapaV[num]) mapaV[num] = { duenoId, boletos: 0, pasajerosQR: 0, ingresos: 0, ticketsSeen: new Set() };
      };
      const ticketsAtribuidos = new Set();

      // Acredita un monto a la urban asignada a un viaje. Devuelve true si se pudo.
      const acreditar = (viaje, monto) => {
        if (!viaje?.duenoId || !viaje?.vehiculoNumero) return false;
        const num = String(viaje.vehiculoNumero);
        agregarUrban(num, viaje.duenoId);
        mapaV[num].boletos++;
        mapaV[num].ingresos += monto;
        return true;
      };

      // Atribución: sencillo → todo a su urban. Redondo → 50% a la urban de la
      // IDA y 50% a la del REGRESO (cada una cobra el tramo que transportó).
      // Si ambos tramos van en la misma urban, esa urban recibe el total.
      ticketDocs.forEach(d => {
        const t = d.data();
        const neto = netoDueno(t);
        let atribuido;
        if (t.tipoViaje === 'redondo') {
          const mitad = neto / 2;
          const okIda = acreditar(viajesMap[t.viajeId], mitad);
          const okReg = acreditar(viajesMap[t.viajeRegresoId], mitad);
          atribuido = okIda || okReg;
        } else {
          atribuido = acreditar(viajesMap[t.viajeId], neto);
        }
        if (atribuido) ticketsAtribuidos.add(d.id);
      });

      // Escaneos QR: contar abordajes por urban
      valSnap.docs.forEach(d => {
        const { vehiculoNumero, ticketId } = d.data();
        if (!vehiculoNumero || !ticketId) return;
        const num = String(vehiculoNumero);
        if (mapaV[num] && !mapaV[num].ticketsSeen.has(ticketId)) {
          mapaV[num].ticketsSeen.add(ticketId);
          mapaV[num].pasajerosQR++;
        }
      });

      const ingresosAtribuidos = Object.values(mapaV).reduce((s, v) => s + v.ingresos, 0);
      setSinAtribuir({ pax: ticketDocs.length - ticketsAtribuidos.size, ingresos: ingresosTotal - ingresosAtribuidos });

      const mapaDueno = {};
      Object.entries(mapaV).forEach(([num, datos]) => {
        const { duenoId, boletos, pasajerosQR, ingresos } = datos;
        if (!mapaDueno[duenoId]) {
          mapaDueno[duenoId] = { dueno: duenosMap[duenoId] || { id: duenoId, nombre: '—' }, vehiculos: {}, totalPax: 0, totalIngresos: 0 };
        }
        mapaDueno[duenoId].vehiculos[num] = { num, boletos, pasajerosQR, ingresos, chofer: choferesMap[num] || null };
        mapaDueno[duenoId].totalPax      += boletos;
        mapaDueno[duenoId].totalIngresos += ingresos;
      });

      const g = Object.values(mapaDueno).reduce(
        (s, d) => ({ pax: s.pax + d.totalPax, ingresos: s.ingresos + d.totalIngresos, urbans: s.urbans + Object.keys(d.vehiculos).length }),
        { pax: 0, ingresos: 0, urbans: 0 }
      );
      setResumen(mapaDueno);
      setGrand(g);
    } catch (err) {
      console.error('Liquidaciones:', err);
    } finally {
      setLoading(false);
    }
  }, [fechaInicio, fechaFin]);

  useEffect(() => { cargar(); }, [cargar]);

  // ── Historial de cortes ────────────────────────────────────────────────────
  const cargarHistorial = useCallback(async () => {
    setLoadingHistorial(true);
    try {
      const snap = await getDocs(query(collection(db, 'cortes'), orderBy('cerradoEn', 'desc')));
      setHistorial(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error('Historial cortes:', err); }
    finally { setLoadingHistorial(false); }
  }, []);

  useEffect(() => { if (tab === 'historial') cargarHistorial(); }, [tab, cargarHistorial]);

  // ── Cerrar período (congelar números en historial) ─────────────────────────
  const cerrarPeriodo = async () => {
    setCerrando(true);
    try {
      const activos = Object.values(resumen).filter(d => d.totalPax > 0 || d.totalIngresos > 0);
      const batch = writeBatch(db);
      activos.forEach(d => {
        const ref = doc(collection(db, 'cortes'));
        batch.set(ref, {
          duenoId:       d.dueno.id,
          duenoNombre:   d.dueno.nombre   || '—',
          duenoTelefono: d.dueno.telefono || null,
          duenoBanco:    d.dueno.banco    || null,
          fechaInicio, fechaFin,
          totalPasajeros: d.totalPax,
          ingresosTotal:  d.totalIngresos,
          vehiculos: Object.values(d.vehiculos).map(v => ({
            num: v.num, boletos: v.boletos, paxQR: v.pasajerosQR, ingresos: v.ingresos, chofer: v.chofer || null,
          })),
          estado: 'pendiente', pagos: [], montoPagado: 0,
          cerradoEn: serverTimestamp(), cerradoPor: user?.uid || null,
        });
      });
      await batch.commit();
      setModalCerrar(false);
      setTab('historial');
    } catch (err) { alert('Error al cerrar período: ' + err.message); }
    finally { setCerrando(false); }
  };

  // ── Registrar pago a un corte ──────────────────────────────────────────────
  const registrarPago = async () => {
    if (!modalPago) return;
    const monto = Number(pagoForm.monto);
    if (!monto || monto <= 0) return;
    setGuardandoPago(true);
    try {
      const corte = historial.find(c => c.id === modalPago.corteId);
      const nuevoMontoPagado = (corte.montoPagado || 0) + monto;
      const nuevoEstado = nuevoMontoPagado >= corte.ingresosTotal ? 'pagado'
                        : nuevoMontoPagado > 0                   ? 'parcial' : 'pendiente';
      await updateDoc(doc(db, 'cortes', modalPago.corteId), {
        pagos: [...(corte.pagos || []), {
          monto, fecha: pagoForm.fecha, metodo: pagoForm.metodo,
          referencia: pagoForm.referencia || null, notas: pagoForm.notas || null,
          registradoEn: new Date().toISOString(),
        }],
        montoPagado: nuevoMontoPagado, estado: nuevoEstado,
      });
      setModalPago(null);
      setPagoForm({ monto: '', fecha: hoy, metodo: 'transferencia', referencia: '', notas: '' });
      cargarHistorial();
    } catch (err) { alert('Error al registrar pago: ' + err.message); }
    finally { setGuardandoPago(false); }
  };

  const duenosActivos   = Object.values(resumen).filter(d => d.totalPax > 0 || d.totalIngresos > 0);
  const duenosFiltrados = filtroUrban ? duenosActivos.filter(d => d.vehiculos[filtroUrban]) : duenosActivos;
  const histFiltrado    = filtroDueno
    ? historial.filter(c => c.duenoNombre?.toLowerCase().includes(filtroDueno.toLowerCase()))
    : historial;

  // ───────────────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto min-h-screen bg-gray-50">

      {/* HEADER */}
      <div className="mb-6 bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Cuentas · Liquidaciones</h1>
          <p className="text-gray-400 text-sm mt-1">{fechaInicio} → {fechaFin}</p>
          <p className="text-[11px] text-gray-300 mt-0.5">Montos: lo que realmente llega · redondo se reparte 50/50 entre la urban de ida y la de regreso</p>
        </div>
        <BotonVolver />
      </div>

      {/* TABS */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6 w-fit">
        {[{ id: 'actual', label: '📊 Período actual' }, { id: 'historial', label: '📋 Historial de pagos' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition ${tab === t.id ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ═══════════ TAB: PERÍODO ACTUAL ═══════════ */}
      {tab === 'actual' && (
        <>
          {/* Filtros */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-6 flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-1 bg-gray-100 rounded-xl px-1 py-1">
              <button onClick={mesAnterior} className="px-2 py-1 text-gray-600 hover:text-gray-900 font-bold text-base rounded-lg hover:bg-gray-200 transition">←</button>
              <span className="font-bold text-gray-800 text-sm min-w-[130px] text-center">{MESES[mesAnio.mes]} {mesAnio.anio}</span>
              <button onClick={mesSiguiente} className="px-2 py-1 text-gray-600 hover:text-gray-900 font-bold text-base rounded-lg hover:bg-gray-200 transition">→</button>
            </div>

            <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
              {[{ id: '1-15', label: '1 — 15' }, { id: '16-fin', label: '16 — fin' }].map(q => (
                <button key={q.id} onClick={() => { setModo('quincena'); setQuincena(q.id); }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-bold transition ${modo === 'quincena' && quincena === q.id ? 'bg-emerald-500 text-white shadow' : 'text-gray-500 hover:text-gray-800'}`}>
                  {q.label}
                </button>
              ))}
              <button onClick={() => setModo('custom')}
                className={`px-3 py-1.5 rounded-lg text-sm font-bold transition ${modo === 'custom' ? 'bg-emerald-500 text-white shadow' : 'text-gray-500 hover:text-gray-800'}`}>
                Rango
              </button>
            </div>

            {modo === 'custom' && (
              <>
                <input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
                <span className="text-gray-400 font-bold">al</span>
                <input type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
                <button onClick={cargar} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-bold text-sm transition">Aplicar</button>
              </>
            )}

            <div className="ml-auto">
              <select value={filtroUrban} onChange={e => setFiltroUrban(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none">
                <option value="">Todas las urbans</option>
                {vehiculos.map(v => <option key={v.id} value={String(v.numero)}>#{v.numero}</option>)}
              </select>
            </div>
          </div>

          {desglose.neto > 0 && (
            <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-4 text-sm">
              <p className="text-[10px] font-bold text-emerald-500 uppercase">Llega</p>
              <p className="font-black text-emerald-700 text-lg">{fmt(desglose.neto)}</p>
            </div>
          )}

          {/* Cards resumen */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Ingresos', val: fmt(grand.ingresos), sub: 'atribuidos a urbans', bg: 'bg-emerald-50 border-emerald-200', tx: 'text-emerald-700', stx: 'text-emerald-500', htx: 'text-emerald-600' },
              { label: 'Pasajeros',      val: grand.pax,            sub: 'boletos del período', bg: 'bg-sky-50 border-sky-200',       tx: 'text-sky-700',     stx: 'text-sky-500',     htx: 'text-sky-600'     },
              { label: 'Urbans Activas', val: grand.urbans,         sub: 'con actividad',       bg: 'bg-violet-50 border-violet-200', tx: 'text-violet-700',  stx: 'text-violet-500',  htx: 'text-violet-600'  },
              { label: 'Sin Atribuir',   val: sinAtribuir.pax,      sub: fmt(sinAtribuir.ingresos), bg: 'bg-orange-50 border-orange-200', tx: 'text-orange-700', stx: 'text-orange-500', htx: 'text-orange-600' },
            ].map(c => (
              <div key={c.label} className={`${c.bg} border rounded-2xl p-4`}>
                <p className={`text-[10px] font-bold ${c.htx} uppercase tracking-wide`}>{c.label}</p>
                <p className={`text-2xl font-black ${c.tx} mt-1 leading-none`}>{c.val}</p>
                <p className={`text-[10px] ${c.stx} mt-1`}>{c.sub}</p>
              </div>
            ))}
          </div>

          {/* Lista de dueños */}
          {loading ? (
            <div className="text-center py-20 text-gray-300 animate-pulse font-bold text-lg">Calculando…</div>
          ) : duenosFiltrados.length === 0 ? (
            <div className="text-center py-20 text-gray-300">
              <p className="text-5xl mb-3">🚌</p>
              <p className="font-bold">Sin actividad atribuida en este período</p>
              <p className="text-sm mt-1">Asigna una urban a cada salida en Taquilla para que el ingreso se acredite al dueño.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {[...duenosFiltrados].sort((a, b) => b.totalIngresos - a.totalIngresos).map(d => {
                const abierto      = expandido === d.dueno.id;
                const vehiculosArr = Object.values(d.vehiculos).sort((a, b) => Number(a.num) - Number(b.num));
                return (
                  <div key={d.dueno.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                    <button className="w-full flex items-center gap-4 p-5 text-left hover:bg-gray-50 transition" onClick={() => setExpandido(abierto ? null : d.dueno.id)}>
                      <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center font-black text-lg shrink-0">
                        {(d.dueno.nombre || '?')[0].toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-800 truncate">{d.dueno.nombre || d.dueno.id}</p>
                        <p className="text-xs text-gray-400 mt-0.5">Urban{vehiculosArr.length !== 1 ? 's' : ''}: {vehiculosArr.map(v => `#${v.num}`).join(' · ')}</p>
                        {d.dueno.banco && <p className="text-[10px] text-gray-400 mt-0.5">🏦 {d.dueno.banco}</p>}
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <div className="text-center hidden sm:block">
                          <p className="text-[10px] text-sky-500 font-bold uppercase">Boletos</p>
                          <p className="font-black text-gray-700 text-lg">{d.totalPax}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[10px] text-emerald-600 font-bold uppercase">A pagar</p>
                          <p className="text-xl sm:text-2xl font-black text-emerald-700">{fmt(d.totalIngresos)}</p>
                        </div>
                        <span className={`text-gray-300 text-xl transition-transform duration-200 ${abierto ? 'rotate-180' : ''}`}>▾</span>
                      </div>
                    </button>

                    {abierto && (
                      <div className="border-t border-gray-100 px-5 pb-5 pt-4">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm min-w-[480px]">
                            <thead>
                              <tr className="text-[10px] text-gray-400 uppercase border-b border-gray-100">
                                <th className="text-left pb-2 font-bold">Urban</th>
                                <th className="text-left pb-2 font-bold">Chofer</th>
                                <th className="text-center pb-2 font-bold text-gray-600">Boletos</th>
                                <th className="text-center pb-2 font-bold text-sky-500">QR ✓</th>
                                <th className="text-right pb-2 font-bold text-emerald-600">A pagar</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {vehiculosArr.map(v => (
                                <tr key={v.num} className="hover:bg-gray-50">
                                  <td className="py-3"><span className="font-black text-gray-800 bg-gray-100 rounded-lg px-2 py-1">#{v.num}</span></td>
                                  <td className="py-3 text-gray-600 text-sm">{v.chofer ? <span className="font-semibold">👤 {v.chofer}</span> : <span className="text-gray-300 italic text-xs">Sin chofer</span>}</td>
                                  <td className="py-3 text-center font-black text-gray-700">{v.boletos}</td>
                                  <td className="py-3 text-center font-bold text-sky-700">{v.pasajerosQR}</td>
                                  <td className="py-3 text-right font-black text-emerald-700">{fmt(v.ingresos)}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="border-t-2 border-gray-200 bg-gray-50">
                                <td colSpan={2} className="pt-3 pb-2 pl-2 text-xs text-gray-400 font-bold uppercase">Subtotal</td>
                                <td className="pt-3 pb-2 text-center font-black text-gray-700">{d.totalPax}</td>
                                <td className="pt-3 pb-2 text-center font-black text-sky-700">{vehiculosArr.reduce((s, v) => s + v.pasajerosQR, 0)}</td>
                                <td className="pt-3 pb-2 text-right font-black text-xl text-emerald-700 pr-1">{fmt(d.totalIngresos)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>

                        {d.dueno.telefono && (
                          <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-dashed border-gray-100">
                            <a
                              href={`https://wa.me/52${d.dueno.telefono.replace(/\D/g, '')}?text=${encodeURIComponent(
                                `Hola ${d.dueno.nombre}, tu liquidación del ${fechaInicio} al ${fechaFin}:\n` +
                                vehiculosArr.map(v => `Urban #${v.num}: ${v.boletos} boletos · ${fmt(v.ingresos)}`).join('\n') +
                                `\nTOTAL: ${d.totalPax} boletos · ${fmt(d.totalIngresos)}`
                              )}`}
                              target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-xs font-bold px-4 py-2 rounded-xl hover:bg-green-100 transition">
                              📱 Enviar por WhatsApp
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {!filtroUrban && duenosFiltrados.length > 0 && (
                <button onClick={() => setModalCerrar(true)}
                  className="w-full py-4 rounded-2xl border-2 border-dashed border-emerald-300 text-emerald-600 font-bold text-sm hover:bg-emerald-50 transition flex items-center justify-center gap-2">
                  🔒 Cerrar período {fechaInicio} → {fechaFin} y guardar en historial
                </button>
              )}

              {sinAtribuir.pax > 0 && !filtroUrban && (
                <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 text-sm text-orange-700">
                  <p className="font-bold">⚠️ {sinAtribuir.pax} boleto{sinAtribuir.pax !== 1 ? 's' : ''} sin atribuir ({fmt(sinAtribuir.ingresos)})</p>
                  <p className="text-xs mt-1 text-orange-600">Son boletos cuyo viaje no tiene una urban asignada. Asígnala en Taquilla.</p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ═══════════ TAB: HISTORIAL ═══════════ */}
      {tab === 'historial' && (
        <>
          <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-6 flex flex-wrap gap-3 items-center">
            <input type="text" placeholder="Buscar dueño..." value={filtroDueno} onChange={e => setFiltroDueno(e.target.value)}
              className="flex-1 min-w-[160px] border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none" />
            <button onClick={cargarHistorial} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl font-bold text-sm transition">🔄 Actualizar</button>
          </div>

          {loadingHistorial ? (
            <div className="text-center py-20 text-gray-300 animate-pulse font-bold">Cargando historial…</div>
          ) : histFiltrado.length === 0 ? (
            <div className="text-center py-20 text-gray-300">
              <p className="text-4xl mb-3">📋</p>
              <p className="font-bold">Sin cortes registrados</p>
              <p className="text-sm mt-1">Cierra un período desde "Período actual".</p>
            </div>
          ) : (
            <div className="space-y-3">
              {histFiltrado.map(corte => {
                const cfg       = ESTADO_CFG[corte.estado] || ESTADO_CFG.pendiente;
                const pendiente = (corte.ingresosTotal || 0) - (corte.montoPagado || 0);
                const abierto   = expandidoCorte === corte.id;
                return (
                  <div key={corte.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                    <button className="w-full flex items-center gap-4 p-5 text-left hover:bg-gray-50 transition" onClick={() => setExpandidoCorte(abierto ? null : corte.id)}>
                      <div className="w-11 h-11 rounded-xl bg-gray-100 text-gray-600 flex items-center justify-center font-black text-lg shrink-0">{(corte.duenoNombre || '?')[0].toUpperCase()}</div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-800 truncate">{corte.duenoNombre}</p>
                        <p className="text-xs text-gray-400 mt-0.5">📅 {corte.fechaInicio} → {corte.fechaFin}</p>
                        {corte.duenoBanco && <p className="text-[10px] text-gray-400 mt-0.5">🏦 {corte.duenoBanco}</p>}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className={`text-[10px] font-black px-2 py-1 rounded-full border uppercase ${cfg.color}`}>{cfg.label}</span>
                        <div className="text-right">
                          <p className="text-[10px] text-gray-400 font-bold uppercase">{corte.estado === 'pagado' ? 'Total pagado' : 'Pendiente'}</p>
                          <p className={`text-xl font-black ${corte.estado === 'pagado' ? 'text-gray-400' : 'text-emerald-700'}`}>{fmt(corte.estado === 'pagado' ? corte.ingresosTotal : pendiente)}</p>
                        </div>
                        <span className={`text-gray-300 text-xl transition-transform duration-200 ${abierto ? 'rotate-180' : ''}`}>▾</span>
                      </div>
                    </button>

                    {abierto && (
                      <div className="border-t border-gray-100 px-5 pb-5 pt-4 space-y-4">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm min-w-[420px]">
                            <thead>
                              <tr className="text-[10px] text-gray-400 uppercase border-b border-gray-100">
                                <th className="text-left pb-2">Urban</th>
                                <th className="text-left pb-2">Chofer</th>
                                <th className="text-center pb-2 text-gray-600">Boletos</th>
                                <th className="text-center pb-2 text-sky-500">QR ✓</th>
                                <th className="text-right pb-2 text-emerald-600">A pagar</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {(corte.vehiculos || []).map(v => (
                                <tr key={v.num}>
                                  <td className="py-2.5"><span className="font-black bg-gray-100 rounded-lg px-2 py-1">#{v.num}</span></td>
                                  <td className="py-2.5 text-gray-500 text-xs">{v.chofer || '—'}</td>
                                  <td className="py-2.5 text-center font-black text-gray-700">{v.boletos ?? 0}</td>
                                  <td className="py-2.5 text-center font-bold text-sky-700">{v.paxQR || 0}</td>
                                  <td className="py-2.5 text-right font-black text-emerald-700">{fmt(v.ingresos)}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="border-t-2 border-gray-200 bg-gray-50">
                                <td colSpan={2} className="pt-2 pb-1 pl-2 text-xs text-gray-400 font-bold uppercase">Total período</td>
                                <td className="pt-2 pb-1 text-center font-black text-gray-700">{(corte.vehiculos || []).reduce((s, v) => s + (v.boletos || 0), 0)}</td>
                                <td className="pt-2 pb-1 text-center font-black text-sky-700">{(corte.vehiculos || []).reduce((s, v) => s + (v.paxQR || 0), 0)}</td>
                                <td className="pt-2 pb-1 text-right font-black text-lg text-emerald-700 pr-1">{fmt(corte.ingresosTotal)}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>

                        {(corte.pagos || []).length > 0 && (
                          <div>
                            <p className="text-xs font-black text-gray-400 uppercase mb-2">Pagos registrados</p>
                            <div className="space-y-2">
                              {corte.pagos.map((p, i) => (
                                <div key={i} className="flex items-start gap-3 bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-3">
                                  <span className="text-emerald-500 mt-0.5">✓</span>
                                  <div className="flex-1">
                                    <p className="font-black text-emerald-800">{fmt(p.monto)}</p>
                                    <p className="text-[11px] text-emerald-600 mt-0.5">{p.fecha} · {p.metodo === 'transferencia' ? '🏦' : '💵'} {p.metodo}{p.referencia ? ` · Ref: ${p.referencia}` : ''}</p>
                                    {p.notas && <p className="text-[11px] text-emerald-500 italic mt-0.5">{p.notas}</p>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {corte.estado !== 'pagado' && (
                          <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
                            <div>
                              <p className="text-xs font-black text-orange-600 uppercase">Pendiente de pago</p>
                              <p className="text-2xl font-black text-orange-700">{fmt(pendiente)}</p>
                            </div>
                            {corte.duenoBanco && <div className="text-right text-xs text-orange-600"><p className="font-bold">🏦 {corte.duenoBanco}</p></div>}
                          </div>
                        )}

                        <div className="flex flex-wrap gap-2">
                          {corte.estado !== 'pagado' && (
                            <button
                              onClick={() => {
                                setModalPago({ corteId: corte.id, pendiente, duenoNombre: corte.duenoNombre });
                                setPagoForm({ monto: String(Math.round(pendiente)), fecha: hoy, metodo: 'transferencia', referencia: '', notas: '' });
                              }}
                              className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold px-4 py-2.5 rounded-xl transition">
                              💳 Registrar pago
                            </button>
                          )}
                          {corte.duenoTelefono && (
                            <a
                              href={`https://wa.me/52${corte.duenoTelefono.replace(/\D/g, '')}?text=${encodeURIComponent(
                                `Hola ${corte.duenoNombre}, tu liquidación del ${corte.fechaInicio} al ${corte.fechaFin}:\n` +
                                (corte.vehiculos || []).map(v => `Urban #${v.num}: ${v.boletos || 0} boletos · ${fmt(v.ingresos)}`).join('\n') +
                                `\nTOTAL: ${corte.totalPasajeros} boletos · ${fmt(corte.ingresosTotal)}` +
                                (corte.estado !== 'pagado' ? `\nPendiente: ${fmt(pendiente)}` : '\n✅ Pagado')
                              )}`}
                              target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-xs font-bold px-4 py-2.5 rounded-xl hover:bg-green-100 transition">
                              📱 WhatsApp
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ═══════════ MODAL: CERRAR PERÍODO ═══════════ */}
      {modalCerrar && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <h2 className="text-xl font-black text-gray-800 mb-1">🔒 Cerrar período</h2>
            <p className="text-sm text-gray-500 mb-5">{fechaInicio} → {fechaFin}</p>
            <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-2 max-h-60 overflow-y-auto">
              {duenosFiltrados.map(d => (
                <div key={d.dueno.id} className="flex justify-between items-center text-sm">
                  <span className="font-semibold text-gray-700 truncate mr-3">{d.dueno.nombre}</span>
                  <span className="font-black text-emerald-700 shrink-0">{fmt(d.totalIngresos)}</span>
                </div>
              ))}
              <div className="border-t border-gray-200 pt-2 flex justify-between items-center">
                <span className="font-black text-gray-600 text-sm uppercase">Total</span>
                <span className="font-black text-emerald-700 text-lg">{fmt(grand.ingresos)}</span>
              </div>
            </div>
            <p className="text-xs text-gray-400 mb-5 leading-relaxed">
              Los números de este período quedan congelados en el historial. Aunque lleguen más ventas, este corte no cambia. El pago lo registras después desde Historial.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setModalCerrar(false)} disabled={cerrando} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50 transition">Cancelar</button>
              <button onClick={cerrarPeriodo} disabled={cerrando} className="flex-1 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm transition disabled:opacity-50">{cerrando ? 'Guardando...' : '✓ Confirmar y cerrar'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ MODAL: REGISTRAR PAGO ═══════════ */}
      {modalPago && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <h2 className="text-xl font-black text-gray-800 mb-1">💳 Registrar pago</h2>
            <p className="text-sm text-gray-500 mb-5">{modalPago.duenoNombre}</p>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Monto pagado</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</span>
                  <input type="number" value={pagoForm.monto} onChange={e => setPagoForm(p => ({ ...p, monto: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl pl-8 pr-4 py-3 text-lg font-black text-emerald-700 focus:outline-none focus:border-emerald-400" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Fecha de pago</label>
                <input type="date" value={pagoForm.fecha} onChange={e => setPagoForm(p => ({ ...p, fecha: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-400" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Método</label>
                <div className="flex gap-2">
                  {METODOS_PAGO.map(m => (
                    <button key={m.id} onClick={() => setPagoForm(p => ({ ...p, metodo: m.id }))}
                      className={`flex-1 py-2 rounded-xl text-xs font-bold transition border ${pagoForm.metodo === m.id ? 'bg-emerald-500 text-white border-emerald-500' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
              {pagoForm.metodo === 'transferencia' && (
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Referencia / Folio bancario</label>
                  <input type="text" value={pagoForm.referencia} placeholder="Ej: BBVA 123456" onChange={e => setPagoForm(p => ({ ...p, referencia: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-400" />
                </div>
              )}
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase block mb-1.5">Notas (opcional)</label>
                <input type="text" value={pagoForm.notas} placeholder="Ej: quincena completa" onChange={e => setPagoForm(p => ({ ...p, notas: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-emerald-400" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setModalPago(null)} disabled={guardandoPago} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50 transition">Cancelar</button>
              <button onClick={registrarPago} disabled={guardandoPago || !pagoForm.monto} className="flex-1 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm transition disabled:opacity-50">{guardandoPago ? 'Guardando...' : '✓ Registrar'}</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
